<template>
  <div
    class="side-menu-v3-wrapper"
    :class="{ 'show-initial-mask': showInitialMask }"
    @focusout="handleSidebarFocusout">
    <zm-skeleton :loading="isLoading" animated>
      <template #template>
        <skeleton-list />
      </template>
      <template #default>
        <div v-if="$slots.header" class="side-menu-v3-wrapper__header">
          <slot name="header" :is-collapsed="false" />
        </div>
        <zm-scrollbar ref="scrollbarRef" class="side-menu-v3-wrapper__body">
          <nav
            ref="mainNavRef"
            class="side-menu-v3__nav"
            :aria-label="ariaLabel || t('aria.sideMenu.mainNav')">
            <side-menu-sub
              v-if="showPinnedSection"
              :key="`pinned-${pinnedProcessedItems.length > 2 ? 'sortable' : 'static'}`"
              :menu-id="`${menuId}-pinned`"
              class="side-menu-v3__list side-menu-v3__list--pinned"
              :is-pinned-menu="true"
              :items="pinnedProcessedItems"
              :level="0"
              :active-key="activeKey"
              :active-item="activeItem"
              :aria-label="t('aria.sideMenu.pinnedSection', [ariaLabel || t('sideMenu.menu')])"
              :has-sub-item="false"
              :pin-able="pinAble"
              :pinned-item-keys="pinnedItemKeysSet"
              :sortable="pinnedProcessedItems.length > 2"
              :suppress-group-hover="isPinnedSorting"
              :is-item-grabbed="isItemGrabbed"
              :context-move-label="contextMoveLabel"
              @item-click="handleItemClick"
              @item-keyboard="handleItemKeydown"
              @item-pin="handleItemPinWithHoverClose"
              @item-hover="showHoverPopup"
              @item-hover-leave="(evt) => onPopupMouseleave(0, evt?.originalEvent)"
              @sort-start="handlePinnedSortStart"
              @sort-end="handlePinnedSortEnd"
              @sortable-reorder="handlePinnedSortableReorder"
              @pinned-handle-keyboard="handlePinnedHandleKeyboard"
              @pinned-handle-focusout="handlePinnedHandleFocusout"
              @pinned-context-move="handlePinnedContextMove"
              @item-expanded-change="emit('item-expanded-change', $event)" />
            <side-menu-sub
              :menu-id="menuId"
              class="side-menu-v3__list"
              :items="processedItems"
              :level="0"
              :active-key="activeKey"
              :active-item="activeItem"
              :aria-label="ariaLabel"
              :pin-able="pinAble"
              :pinned-item-keys="pinnedItemKeysSet"
              @item-click="handleItemClick"
              @item-keyboard="handleItemKeydown"
              @item-pin="handleItemPinWithHoverClose"
              @item-hover="showHoverPopup"
              @item-hover-leave="(evt) => onPopupMouseleave(0, evt?.originalEvent)"
              @item-expanded-change="emit('item-expanded-change', $event)" />
          </nav>
        </zm-scrollbar>

        <div v-if="$slots.footer" class="side-menu-v3-wrapper__footer">
          <slot name="footer" />
        </div>
      </template>
    </zm-skeleton>

    <Teleport to="body">
      <template v-for="(stackItem, index) in hoverStack" :key="index">
        <Transition name="side-menu-v3-hover-popup">
          <div
            v-if="stackItem"
            :ref="
              (el) => {
                if (el) hoverPopupEls[index] = el
                else delete hoverPopupEls[index]
              }
            "
            class="side-menu-v3__hover-popup"
            :style="stackItem.style"
            role="group"
            :aria-label="stackItem.processedItem.item?.label || t('aria.sideMenu.submenu')"
            @mouseenter="onPopupMouseenter(index)"
            @mouseleave="(evt) => onPopupMouseleave(index, evt)">
            <zm-scrollbar
              class="side-menu-v3__hover-popup-scrollbar"
              wrap-class="side-menu-v3__hover-popup-scrollbar-wrap"
              always>
              <side-menu-sub
                :menu-id="`${menuId}-hover-${index}`"
                class="side-menu-v3__list side-menu-v3__hover-popup-list"
                :items="stackItem.processedItem.items"
                :level="0"
                :active-key="activeKey"
                :active-item="activeItem"
                :templates="$slots"
                :has-sub-item="false"
                :pin-able="pinAble"
                :pinned-item-keys="pinnedItemKeysSet"
                :hover-all-groups="true"
                :is-in-hover-popup="true"
                @item-click="handleHoverPopupItemClick"
                @item-pin="handleItemPin"
                @item-hover="(evt) => handlePopupItemHover(index, evt)"
                @item-hover-leave="(evt) => onPopupItemHoverLeave(index, evt?.originalEvent)"
                @item-expanded-change="emit('item-expanded-change', $event)" />
            </zm-scrollbar>
          </div>
        </Transition>
      </template>
    </Teleport>

    <zm-context-menu-holder />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useId,
  useTemplateRef,
  toRef
} from 'vue'
import { announce, ZmScrollbar, ZmSkeleton, ZmContextMenuHolder } from '@zoom/zoom-ui-vue3'
import SideMenuSub from './side-menu-sub.vue'
import SkeletonList from './skeleton-list.vue'
import { useTree } from './composables/use-tree'
import { useA11y } from './composables/use-a11y'
import { usePinnedMenus } from './composables/use-pinned'
import { usePinnedReorder } from './composables/use-pinned-reorder'
import i18n from '../../../shared/i18n'

const { t } = i18n.global

defineOptions({ name: 'side-menu-v3' })

const props = defineProps({
  menus: { type: Array, required: true },
  activeKey: { type: String, default: null },
  pinAble: { type: Boolean, default: false },
  pinnedMenuIds: { type: Array, default: () => [] },
  isMobile: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  showInitialMask: { type: Boolean, default: false },
  ariaLabel: String,
  /** When true (default), expanding a group auto-collapses its siblings. */
  accordion: { type: Boolean, default: true },
  adjust: Boolean,
  width: { type: Number, default: 280 },
  minWidth: { type: Number, default: 200 },
  maxWidth: { type: Number, default: 400 },
  /** Label of the "Move item" entry inside the pinned right-click context menu. */
  contextMoveLabel: { type: String, default: () => i18n.global.t('sideMenu.moveItem') }
})

const emit = defineEmits(['update:pinnedMenuIds', 'item-expanded-change', 'item-click'])

const menuId = useId()
const scrollbarRef = useTemplateRef('scrollbarRef')
const mainNavRef = useTemplateRef('mainNavRef')

// Stable empty Set passed to hover popups (avoids creating a new Set on every render)
const activeItem = ref()

// hoverStack: [{ processedItem, triggerEl, style }], index 0 = sidebar trigger, index N = nested popup trigger
const hoverStack = ref([])
// hoverPopupEls: { [index]: DOMElement } assigned via template callback refs (non-reactive, for positioning only)
const hoverPopupEls = {}
let _hoverShowTimer = null
let _hoverHideTimer = null
let _scrollbarWrapEl = null
// Clicking a leaf that navigates away should not trigger blur-time accordion restore.
const skipNextFocusoutRestore = ref(false)

// Sync `is-hover` class on trigger elements as the popup stack changes.
// Only re-runs when the set of triggerEls actually changes (not on style corrections).
watch(
  () => hoverStack.value.map((item) => item?.triggerEl),
  (newEls, oldEls) => {
    const next = new Set(newEls.filter(Boolean))
    const prev = new Set((oldEls ?? []).filter(Boolean))
    prev.forEach((el) => {
      if (!next.has(el)) el.classList.remove('is-hover')
    })
    next.forEach((el) => el.classList.add('is-hover'))
  }
)

const getScrollWrap = () => scrollbarRef.value?.getScrollContainer?.()

// Accordion: when about to expand `item`, collapse its already-expanded
// siblings. Works for both main and pinned scopes — each lives in its own
// reactive state container (`useTree` _snapshot vs. `pinnedExpandedKeys`), so
// we route the collapse call by scope.
// Scope is carried explicitly by `_pinnedScope: true` stamped on every node in
// the pinned tree (see buildPinnedProcessedItems). We do NOT rely on the
// nodeKey prefix or on `_pinned` (which only marks the root-level pinned rows,
// not their descendants).
const isPinnedScopeItem = (processedItem) => !!processedItem?.item?._pinnedScope

const collapseAccordionSiblings = (item) => {
  if (!props.accordion) return
  const isPinned = isPinnedScopeItem(item)
  const rootList = isPinned ? pinnedProcessedItems.value : processedItems.value
  const siblings = item.parent ? item.parent.items : rootList
  siblings?.forEach((s) => {
    if (s.key === item.key || !s.expanded) return
    if (isPinned) togglePinnedExpansion(s, false)
    else toggleTreeExpansion(s, false)
  })
}

const clearHoverTimers = () => {
  clearTimeout(_hoverShowTimer)
  clearTimeout(_hoverHideTimer)
}

const closeAllHoverPopups = () => {
  clearHoverTimers()
  hoverStack.value = []
}

/**
 * Pick the best popup top position to avoid internal scrollbars:
 * 1. Align top with trigger (preferred when there is enough space below)
 * 2. Align bottom with trigger (when there is enough space above)
 * 3. Center on trigger, clamped to viewport
 */
const getOptimalTop = (triggerRect, popupHeight) => {
  const vh = window.innerHeight
  const MARGIN = 8
  const spaceBelow = vh - triggerRect.top - MARGIN
  const spaceAbove = triggerRect.bottom - MARGIN

  if (popupHeight <= spaceBelow) return triggerRect.top
  if (popupHeight <= spaceAbove) return triggerRect.bottom - popupHeight
  const centered = triggerRect.top + (triggerRect.height - popupHeight) / 2
  return Math.max(MARGIN, Math.min(vh - popupHeight - MARGIN, centered))
}

// Uses the sidebar container's right edge rather than the item's, for accurate popup offset
const getSidebarRight = (triggerEl) => {
  const wrapper = triggerEl.closest('.side-menu-v3-wrapper')
  return (wrapper ?? triggerEl).getBoundingClientRect().right
}

// Estimate popup height for first-frame positioning (title 42px + item 36px each + padding 16px, max 60vh)
const estimatePopupHeight = (processedItem) => {
  const ITEM_H = 36,
    HEADER_H = 42,
    POPUP_PD = 16
  return Math.min(
    (processedItem?.items?.length ?? 0) * ITEM_H + HEADER_H + POPUP_PD,
    window.innerHeight * 0.6
  )
}

const getPopupLeft = (level, triggerEl) => {
  if (level === 0) return getSidebarRight(triggerEl) + 4
  const prevEl = hoverPopupEls[level - 1]
  return (prevEl?.getBoundingClientRect().right ?? triggerEl.getBoundingClientRect().right) + 4
}

// Recursively reset expanded to false so hover popup items always start collapsed,
// independent of the main menu tree's expansion state (the objects are shared references).
const withCollapsedItems = (item) => ({
  ...item,
  expanded: false,
  items: item.items?.map(withCollapsedItems)
})

/**
 * Open the hover popup at the given stack level.
 * Debounced 200ms on first open; immediate when a popup is already showing.
 * Positions with an estimated height first, then corrects after render.
 */
const openHoverAt = (level, { processedItem, triggerEl }) => {
  clearHoverTimers()
  const delay = hoverStack.value.length > 0 ? 0 : 200
  hoverStack.value = hoverStack.value.slice(0, level)

  _hoverShowTimer = setTimeout(async () => {
    const rect = triggerEl.getBoundingClientRect()
    const estimatedStyle = {
      top: `${getOptimalTop(rect, estimatePopupHeight(processedItem))}px`,
      left: `${getPopupLeft(level, triggerEl)}px`
    }
    hoverStack.value = [
      ...hoverStack.value.slice(0, level),
      { processedItem: withCollapsedItems(processedItem), triggerEl, style: estimatedStyle }
    ]

    await nextTick()

    const popupEl = hoverPopupEls[level]
    if (popupEl) {
      hoverStack.value[level] = {
        ...hoverStack.value[level],
        style: {
          top: `${getOptimalTop(rect, popupEl.offsetHeight)}px`,
          left: `${getPopupLeft(level, triggerEl)}px`
        }
      }
    }
  }, delay)
}

const onPopupMouseenter = (level) => {
  clearHoverTimers()
  if (hoverStack.value.length > level + 1) {
    hoverStack.value = hoverStack.value.slice(0, level + 1)
  }
}

const isTooltipNode = (node) => !!node?.closest?.('[role="tooltip"]')

const getPopupLevelByNode = (node) => {
  if (!node) return -1
  for (const [key, el] of Object.entries(hoverPopupEls)) {
    if (el?.contains?.(node)) return Number(key)
  }
  return -1
}

const onPopupMouseleave = (level, event) => {
  const relatedTarget = event?.relatedTarget
  const targetPopupLevel = getPopupLevelByNode(relatedTarget)
  if (targetPopupLevel >= level + 1 || (relatedTarget && isTooltipNode(relatedTarget))) return
  clearHoverTimers()
  _hoverHideTimer = setTimeout(() => {
    hoverStack.value = hoverStack.value.slice(0, level)
  }, 150)
}

const onPopupItemHoverLeave = (level, event) => {
  const relatedTarget = event?.relatedTarget
  const targetPopupLevel = getPopupLevelByNode(relatedTarget)
  if (targetPopupLevel >= level + 1 || (relatedTarget && isTooltipNode(relatedTarget))) return
  clearHoverTimers()
  _hoverHideTimer = setTimeout(() => {
    hoverStack.value = hoverStack.value.slice(0, level + 1)
  }, 150)
}

const showHoverPopup = ({ triggerEl, processedItem }) => {
  if (props.isMobile) return
  if (isPinnedSorting.value) return
  if (processedItem.expanded) return
  openHoverAt(0, { processedItem, triggerEl })
}

const handlePopupItemHover = (level, { processedItem, triggerEl }) => {
  if (isPinnedSorting.value) return
  openHoverAt(level + 1, { processedItem, triggerEl })
}

const onScrollbarScroll = () => {
  if (!hoverStack.value.length || !_scrollbarWrapEl) return
  const level0 = hoverStack.value[0]
  if (!level0) return
  const triggerRect = level0.triggerEl.getBoundingClientRect()
  const wrapRect = _scrollbarWrapEl.getBoundingClientRect()
  if (triggerRect.bottom < wrapRect.top || triggerRect.top > wrapRect.bottom) {
    closeAllHoverPopups()
  } else {
    const popupEl = hoverPopupEls[0]
    const popupH = popupEl?.offsetHeight ?? estimatePopupHeight(level0.processedItem)
    hoverStack.value[0] = {
      ...hoverStack.value[0],
      style: {
        top: `${getOptimalTop(triggerRect, popupH)}px`,
        left: `${getSidebarRight(level0.triggerEl) + 4}px`
      }
    }
  }
}

const {
  processedItems,
  toggleTreeExpansion,
  expandKeys,
  findMenu,
  findMenuContext,
  findProcessedItem,
  handleFocusout
} = useTree(props, activeItem)

const { handleItemKeydown: _a11yHandleItemKeydown, buildA11y } = useA11y(
  { autoActive: true },
  () => {},
  toggleTreeExpansion,
  activeItem
)

const handleItemKeydown = async (event) => {
  const { processedItem, originalEvent } = event
  const keyCode = originalEvent.code
  if (processedItem.item?._pinned && originalEvent.altKey) {
    if (keyCode === 'ArrowUp' || keyCode === 'ArrowDown') {
      const direction = keyCode === 'ArrowUp' ? -1 : 1
      const result = handleKeyboardReorder({ menuItem: processedItem.item, direction })
      originalEvent.stopPropagation()
      originalEvent.preventDefault()
      if (result?.moved) {
        const label = processedItem.item?.label ?? t('item')
        const key = direction < 0 ? 'sideMenu.movedUpNew' : 'sideMenu.movedDownNew'
        announce(t(key, [label, result.newIndex + 1, result.total]))
        await nextTick()
        if (processedItem.item?.id) {
          document.getElementById(String(processedItem.item.id))?.focus()
        }
      }
      return
    }
  }
  closeAllHoverPopups()

  // All group expansion / collapse via keyboard is owned HERE rather than
  // delegated to `useA11y`. Reasons:
  //   1. `useTree#toggleTreeExpansion` only knows about main menu's _snapshot.
  //      Pinned items live in `pinnedExpandedKeys` and must route through
  //      `togglePinnedExpansion`, otherwise pinned groups never toggle.
  //   2. We must `preventDefault` so Enter on an `<a href>` group does NOT
  //      synthesise a default click and navigate the page.
  //   3. Accordion: expanding a group must collapse already-expanded siblings,
  //      and useA11y's Arrow toggle path bypasses our accordion helper.
  // useA11y still owns the *focus traversal* arrow paths (Right-on-expanded,
  // Left-on-collapsed/subtree) which don't depend on toggleTreeExpansion.
  if (processedItem.items?.length) {
    const isPinned = !!processedItem.item?._pinned
    const expanded = processedItem.expanded
    const doToggle = (next) => {
      if (isPinned) togglePinnedExpansion(processedItem, next)
      else toggleTreeExpansion(processedItem, next)
    }

    if (keyCode === 'Space' || keyCode === 'Enter') {
      originalEvent.preventDefault()
      originalEvent.stopPropagation()
      if (!expanded) collapseAccordionSiblings(processedItem)
      doToggle()
      return
    }
    if (keyCode === 'ArrowRight' && !expanded) {
      collapseAccordionSiblings(processedItem)
      doToggle(true)
      originalEvent.preventDefault()
      originalEvent.stopPropagation()
      return
    }
    if (keyCode === 'ArrowLeft' && expanded) {
      doToggle(false)
      originalEvent.preventDefault()
      originalEvent.stopPropagation()
      return
    }
  }

  // Leaf node + Space: browsers natively activate <a href> on Enter but scroll
  // on Space. Simulate a click so Space triggers navigation the same as Enter.
  if (keyCode === 'Space' && processedItem.item?.url) {
    originalEvent.preventDefault()
    const target = originalEvent.currentTarget ?? originalEvent.target
    target?.click()
    return
  }

  return _a11yHandleItemKeydown(event)
}

const {
  pinnedMenus,
  syncPinnedMenus,
  handleItemPin,
  handleKeyboardReorder,
  handleSortableReorder,
  isItemGrabbed,
  releasePinnedGrab,
  handlePinnedHandleKeydown
} = usePinnedMenus({
  menus: toRef(props, 'menus'),
  findMenu,
  findMenuContext,
  pinnedMenuIds: toRef(props, 'pinnedMenuIds'),
  onPinnedMenuIdsUpdate: (ids) => emit('update:pinnedMenuIds', ids)
})

const pinnedItemKeysSet = computed(() => {
  const keys = pinnedMenus.value.map((m) => {
    const k = m.key ?? m.id
    return k?.startsWith('pinned-') ? k.slice('pinned-'.length) : k
  })
  return new Set(keys)
})

const showPinnedSection = computed(() => props.pinAble && pinnedMenus.value.length > 0)
const pinnedExpandedKeys = ref(new Set())

const getPinnedItemKey = (item) => item?.key ?? item?.id
const normalizePinnedKey = (key) => (key?.startsWith('pinned-') ? key.slice('pinned-'.length) : key)
const toStableNodeSegment = (item, fallbackIndex) => {
  const rawKey = normalizePinnedKey(getPinnedItemKey(item))
  return rawKey == null || rawKey === '' ? `idx_${fallbackIndex}` : String(rawKey)
}

const togglePinnedExpansion = (processedItem, expanded) => {
  const key = getPinnedItemKey(processedItem?.item)
  if (!key) return
  const next = new Set(pinnedExpandedKeys.value)
  const nextExpanded = expanded ?? !processedItem.expanded
  if (nextExpanded) next.add(key)
  else next.delete(key)
  pinnedExpandedKeys.value = next
}

const buildPinnedProcessedItems = (items, level = 0, parent = undefined, parentKey = '') => {
  const list = items.map((item, index) => {
    const segment = toStableNodeSegment(item, index)
    const nodeKey = parentKey ? `${parentKey}/${segment}` : `pinned/${segment}`
    const menuKey = normalizePinnedKey(getPinnedItemKey(item))
    const pinnedItem = {
      ...item,
      _pinned: !!(menuKey && pinnedItemKeysSet.value.has(menuKey)),
      // Every node in the pinned tree carries this flag, including descendants
      // of pinned root groups. Consumed by `isPinnedScopeItem` to decide which
      // toggle / accordion path to take. Decoupled from `_pinned` (root-only).
      _pinnedScope: true
    }
    const node = {
      item: pinnedItem,
      index,
      level,
      key: nodeKey,
      parent,
      parentKey,
      expanded: pinnedExpandedKeys.value.has(getPinnedItemKey(pinnedItem))
    }
    node.items = buildPinnedProcessedItems(item.items ?? [], level + 1, node, nodeKey)
    return node
  })
  if (level === 0 && list.length) {
    list.unshift({
      item: {
        _pinned: true,
        _pinnedScope: true,
        label: t('sideMenu.pinned')
      },
      index: 0,
      level: 0,
      key: 'pinned_0',
      parent: undefined,
      parentKey: ''
    })
  }

  return list
}

const pinnedProcessedItems = computed(() => buildPinnedProcessedItems(pinnedMenus.value))

const {
  isPinnedSorting,
  handlePinnedSortStart,
  handlePinnedSortEnd,
  handlePinnedSortableReorder,
  handlePinnedHandleKeyboard,
  handlePinnedHandleFocusout,
  handlePinnedContextMove
} = usePinnedReorder({
  pinnedMenus,
  handleSortableReorder,
  handlePinnedHandleKeydown,
  isItemGrabbed,
  releasePinnedGrab,
  closeAllHoverPopups
})

const handleSidebarFocusout = (e) => {
  if (skipNextFocusoutRestore.value) {
    skipNextFocusoutRestore.value = false
    return
  }
  handleFocusout(e)
}

const handleItemClick = async (event) => {
  const { processedItem } = event
  closeAllHoverPopups()

  if (processedItem.items?.length) {
    if (!processedItem.expanded) collapseAccordionSiblings(processedItem)
    if (processedItem.item?._pinned) togglePinnedExpansion(processedItem)
    else toggleTreeExpansion(processedItem)
    return
  }

  if (processedItem.item.url && processedItem.item.target !== '_blank') {
    skipNextFocusoutRestore.value = true
    emit('item-click', { processedItem })
    return
  }

  await nextTick()
  activeItem.value = processedItem
  emit('item-click', { processedItem })
}

const handleItemPinWithHoverClose = (event) => {
  closeAllHoverPopups()
  handleItemPin(event)
}

const focusMainSidebarTreeitem = (processedItem) => {
  const menuId = processedItem?.item?.id
  if (menuId == null || menuId === '') return
  const el = document.getElementById(String(menuId))
  const nav = mainNavRef.value
  if (el && nav?.contains(el)) el.focus()
}

const handleHoverPopupItemClick = async ({ processedItem: child }) => {
  if (child.items?.length) return

  closeAllHoverPopups()
  if (child.item?.url && child.item?.target !== '_blank') {
    skipNextFocusoutRestore.value = true
    return
  }

  const chain = []
  let cur = child
  while (cur) {
    chain.unshift(cur)
    cur = cur.parent
  }

  const expandWithAccordion = (item) => {
    if (!item.items?.length || item.expanded) return
    collapseAccordionSiblings(item)
    toggleTreeExpansion(item, true)
  }

  chain.slice(0, -1).forEach(expandWithAccordion)
  await nextTick()
  activeItem.value = child
  scrollToActive()
  focusMainSidebarTreeitem(child)
}

watch(() => props.menus, syncPinnedMenus, { immediate: true })
watch(() => props.pinnedMenuIds, syncPinnedMenus)

const scrollToActive = () => {
  const isVisibleElement = (el) =>
    !!el && el.offsetParent !== null && el.getClientRects().length > 0

  const getActiveElement = (root) => {
    const activeCandidates = root?.querySelectorAll('.side-menu-v3__item-content.is-active')
    if (!activeCandidates?.length) return null
    return Array.from(activeCandidates).find(isVisibleElement) ?? activeCandidates[0]
  }

  setTimeout(() => {
    const scrollWrap = getScrollWrap()
    const activeEl = getActiveElement(scrollWrap)
    if (!activeEl) return
    scrollbarRef.value?.scrollYIntoView?.(activeEl, 300, 64)
  }, 0)
}

watch(() => props.activeKey, scrollToActive)

watch(
  activeItem,
  (val, old) => {
    if (val && !old) scrollToActive()
  },
  { once: true }
)

const onDocumentClick = (e) => {
  if (!hoverStack.value.length) return
  const target = e.target
  const inSidebar = scrollbarRef.value?.$el?.contains(target)
  const inPopup = Object.values(hoverPopupEls).some((el) => el.contains(target))
  if (!inSidebar && !inPopup) closeAllHoverPopups()
}

onMounted(() => {
  const wrap = getScrollWrap()
  if (wrap) {
    _scrollbarWrapEl = wrap
    wrap.addEventListener('scroll', onScrollbarScroll, { passive: true })
  }
  document.addEventListener('click', onDocumentClick, { capture: true })
})

onBeforeUnmount(() => {
  clearHoverTimers()
  if (_scrollbarWrapEl) {
    _scrollbarWrapEl.removeEventListener('scroll', onScrollbarScroll)
    _scrollbarWrapEl = null
  }
  document.removeEventListener('click', onDocumentClick, { capture: true })
})

watch(processedItems, async () => {
  await nextTick()
  buildA11y()
})

watch(pinnedProcessedItems, async () => {
  await nextTick()
  buildA11y()
})

const reset = () => {
  activeItem.value = undefined
}

const setActive = async (keyOrId) => {
  const item = findProcessedItem(keyOrId)
  if (!item) return
  const ancestorKeys = []
  let ancestor = item.parent
  while (ancestor) {
    ancestorKeys.push(ancestor.item.key ?? ancestor.item.id)
    ancestor = ancestor.parent
  }
  expandKeys(ancestorKeys)

  await nextTick()
  activeItem.value = item
  scrollToActive()
}

const menuRef = {
  toggleExpand: (keyOrId, expanded) => {
    const item = findProcessedItem(keyOrId)
    if (item) toggleTreeExpansion(item, expanded)
  }
}

defineExpose({ reset, setActive, menuRef, skipNextFocusoutRestore })
</script>

<style lang="scss">
.side-menu-v3-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 300px;
  position: relative;
  border-right: 1px solid var(--zoom-color-border-subtle-neutral);

  &__header {
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-height: 0;
  }

  &__footer {
    flex-shrink: 0;
    padding: 8px;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral);
  }

  &__skeleton {
    padding: 4px 8px;
  }

  &.show-initial-mask {
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 10;
      pointer-events: all;
    }
  }
}

@media screen and (max-width: 768px) {
  .side-menu-v3-wrapper {
    width: 100%;
  }
}
.side-menu-v3__nav {
  padding: 8px 4px;
}

.side-menu-v3__list {
  list-style: none;
  margin: 0;
  padding: 0 4px;
}

.side-menu-v3-collapse-enter-active,
.side-menu-v3-collapse-leave-active {
  transition:
    max-height 0.25s ease-in-out,
    padding-top 0.25s ease-in-out,
    padding-bottom 0.25s ease-in-out;
}

.side-menu-v3__item-content {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  color: var(--zoom-color-text-stronger-neutral);
  outline-offset: 2px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent !important;
  user-select: none;
  font-size: 14px;
  line-height: 20px;

  &:focus {
    text-decoration: none;
    outline: none;
  }

  &:hover,
  &.is-hover {
    // Apply hover styles only on devices that genuinely support hover (mouse/trackpad).
    @media (hover: hover) {
      background-color: var(--zoom-color-state-subtle-neutral-hover);
      color: var(--zoom-color-text-stronger-neutral);
      text-decoration: none;

      .side-menu-v3__item-pin-button {
        opacity: 1;
      }
    }

    // On touch-only devices (hover: none) the browser synthesizes :hover from the first
    // tap. If ANY visual change occurs iOS treats that tap as "hover activation" and
    // suppresses the click, requiring a second tap to navigate.
    // Explicitly reset every property that might be changed by a global CSS rule
    // (e.g. `a:hover { text-decoration: underline; color: ... }` in the CDN stylesheet)
    // so the element looks identical before and after the synthesized :hover fires.
    // Use !important to win over external rules like `.nav-menu a:hover` which have
    // higher specificity (0,2,1) than our (0,2,0) rule.
    @media (hover: none) {
      background-color: transparent !important;
      color: var(--zoom-color-text-stronger-neutral) !important;
      text-decoration: none !important;
    }
  }

  &:active {
    background-color: var(--zoom-color-state-subtle-neutral-press);
    color: var(--zoom-color-text-stronger-neutral);
    text-decoration: none;
  }

  &.is-disabled {
    color: var(--zoom-color-state-disable);
    cursor: not-allowed;

    &:hover,
    &:active {
      background-color: transparent;
    }
  }

  &.is-muted {
    color: var(--zoom-color-text-neutral);
    font-size: 12px;
    line-height: 16px;
  }

  &.is-active {
    background-color: var(--zoom-color-fill-subtler-primary);
    color: var(--zoom-color-text-primary);

    &.is-disabled {
      background-color: var(--zoom-color-toggle-button-background-selected);
      color: var(--zoom-color-state-subtle-primary-disable);
    }
  }

  &.is-separator {
    height: 0;
    min-height: 0;
    margin: 10px 8px 0;
    padding: 0 0 8px;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral);
    border-radius: unset;
    cursor: default;

    &:hover,
    &:active {
      background-color: transparent;
    }
  }

  &.is-title {
    margin-top: 12px;
    cursor: text;
    user-select: auto;

    &:hover,
    &:active {
      background-color: transparent;
    }
  }
}

.side-menu-v3__list--pinned {
  border-bottom: 1px solid var(--zoom-color-border-subtle-neutral);
  padding-bottom: 8px;
  margin-bottom: 12px;
  .side-menu-v3__item-content.is-title {
    margin-top: 0;
  }
}
.side-menu-v3__item {
  list-style: none;
  &.sortable-drag {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: var(--zoom-box-shadow-small);
  }
}
.side-menu-v3__item:has(#__pinned_group__) {
  border-bottom: 1px solid var(--zoom-color-border-subtle-neutral);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.side-menu-v3__item + .side-menu-v3__item {
  margin-top: 2px;
}

.side-menu-v3__item.is-sortable-draggable {
  cursor: grab;
}

.side-menu-v3__item.is-drag-placeholder-row > .side-menu-v3__item-content,
.side-menu-v3__item-content.is-drag-placeholder-row {
  background: var(--zoom-color-state-subtle-primary-hover);
  border-color: transparent;

  * {
    opacity: 0;
  }
}

.side-menu-v3__item-icon {
  flex-shrink: 0;
  margin-right: 8px;
  font-size: 16px;
}

.side-menu-v3__item-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  outline: none;
  cursor: grab;
  // WCAG 2.5.8 (AA, WCAG 2.2): pointer target at least 24×24 CSS pixels.
  // Use padding + negative margin so the visual position of the icon is
  // unchanged while the hit area expands beyond the icon glyph.
  min-width: 24px;
  min-height: 24px;
  padding: 4px;
  margin: -4px 4px -4px -4px;

  &:focus-visible {
    outline: 2px solid var(--zoom-color-border-primary);
    outline-offset: 2px;
  }

  &.is-grabbed {
    cursor: grabbing;
    background-color: var(--zoom-color-state-subtle-primary-hover);
    color: var(--zoom-color-text-primary);
  }
  .side-menu-v3__item-icon {
    margin-right: 0;
  }
}

.side-menu-v3__item-content:hover .side-menu-v3__item-handle,
.side-menu-v3__item-handle:focus-visible {
  // While hovered or focused via keyboard, surface the drag handle affordance
  color: var(--zoom-color-text-secondary-neutral);
}

.side-menu-v3__item-external-icon {
  flex-shrink: 0;
  margin-left: 4px;
  font-size: 14px;
  color: var(--zoom-color-text-secondary-neutral, #747487);
}

.side-menu-v3__item-label {
  flex: 1;
  white-space: initial;
  display: flex;
  flex-wrap: balance;
}

.side-menu-v3__item-sub-label {
  color: var(--zoom-color-text-neutral);
  font-size: 12px;
}
.side-menu-v3__item-label-text {
  margin-right: 0;
}
.side-menu-v3__item-label:has(.side-menu-v3__item-sub-label) {
  .side-menu-v3__item-label-text {
    margin-right: 4px;
  }
}
.side-menu-v3__item-title {
  flex: 1;
  color: var(--zoom-color-text-neutral);
  font-size: 12px;
}

.is-level-1 {
  padding-left: 24px;
  padding-right: 0;
}
.is-level-2 {
  padding-left: 16px;
  padding-right: 0;
}

.side-menu-v3__item-badge {
  margin-left: 8px;
  flex-shrink: 0;
}

.side-menu-v3__item-content .side-menu-v3__item-pin-button {
  flex-shrink: 0;
  margin-left: 4px;
  opacity: 0;
  transition: opacity 0.15s;
  width: 20px;
  height: 20px;
  color: inherit;
  &:focus {
    opacity: 1;
  }
}
.side-menu-v3__item-content:hover {
  .side-menu-v3__item-pin-button {
    opacity: 1;
  }
}
.side-menu-v3__submenu-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease-in-out;
  transform: rotate(-90deg);
  margin-left: 4px;
  &--expanded {
    transform: rotate(0);
  }
}

.side-menu-v3__skeleton-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;

  & + & {
    margin-top: 16px;
  }

  &:first-child {
    margin-top: 12px;
  }
}

.side-menu-v3__skeleton-label {
  flex: 1;
}

.is-vue3-keyboard-event {
  .side-menu-v3__item-content:focus {
    outline: 2px solid var(--zoom-color-border-primary);
    outline-offset: 2px;
  }
}

.side-menu-v3-list-move,
.side-menu-v3-list-enter-active,
.side-menu-v3-list-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.side-menu-v3-list-enter-from,
.side-menu-v3-list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.side-menu-v3-list-leave-active {
  position: absolute;
}

.side-menu-v3__hover-popup {
  position: fixed;
  z-index: 2001;
  min-width: 200px;
  max-width: 300px;
  max-height: 60vh;
  overflow: hidden;
  background: var(--zoom-color-bg-raised, #fff);
  border-radius: 16px;

  padding: 8px 0;
  box-shadow:
    0 12px 24px 0 var(--zoom-color-underlay-dropShadow),
    0 6px 12px 0 var(--zoom-color-underlay-dropShadow);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--zoom-color-border-subtle-neutral);

  &-scrollbar {
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }
  &-scrollbar-wrap {
    max-height: calc(60vh - 16px - 2px);
  }

  &-list.side-menu-v3__list {
    padding: 0 8px;
  }
}

.side-menu-v3-hover-popup-enter-active,
.side-menu-v3-hover-popup-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.side-menu-v3-hover-popup-enter-from,
.side-menu-v3-hover-popup-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

body.side-menu-v3-context-dragging {
  cursor: grabbing;

  .side-menu-v3__list:not(.side-menu-v3__list--pinned),
  .side-menu-v3__hover-popup {
    pointer-events: none;
  }
}

// WCAG 2.3.3 / 2.2.2 - honour OS-level "reduce motion" preference by
// collapsing all menu transitions / animations to instant changes.
@media (prefers-reduced-motion: reduce) {
  .side-menu-v3-collapse-enter-active,
  .side-menu-v3-collapse-leave-active,
  .side-menu-v3-list-move,
  .side-menu-v3-list-enter-active,
  .side-menu-v3-list-leave-active,
  .side-menu-v3-hover-popup-enter-active,
  .side-menu-v3-hover-popup-leave-active,
  .side-menu-v3__submenu-icon,
  .side-menu-v3__item-content,
  .side-menu-v3__item-pin-button {
    transition: none !important;
    animation: none !important;
  }
}
</style>
